<template>
  <div class="settings-page">
    <div class="mb-5">
      <span class="text-h5">Settings</span>
    </div>

    <div class="setting-card">
      <div class="setting-info">
        <div class="setting-title">Auto-close Inactive cases</div>
        <div class="setting-desc">
          Cases with no activity for this many days are marked Auto-closed and
          excluded from AI knowledge re-ingestion.
        </div>
      </div>

      <div class="setting-controls">
        <v-text-field
          v-model="autoCloseDays"
          class="days-input"
          type="number"
          min="1"
          variant="outlined"
          density="compact"
          hide-details
          :disabled="loading"
          @keydown="allowNumbersOnly"
        />
        <span class="days-label">days</span>
        <v-btn
          color="primary"
          variant="flat"
          class="text-none save-btn"
          :loading="saving"
          :disabled="loading"
          @click="saveSettings"
        >
          Save
        </v-btn>
      </div>
    </div>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      location="top"
    >
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import { generateClient } from "aws-amplify/api";
const client = generateClient();
import { get_auto_close_days } from "@/graphql/queries";
import { set_auto_close_days } from "@/graphql/mutations";

export default {
  name: "RCSSupportSettings",
  data() {
    return {
      autoCloseDays: 7,
      loading: false,
      saving: false,
      snackbar: {
        show: false,
        message: "",
        color: "success",
      },
    };
  },
  async mounted() {
    await this.loadSettings();
  },
  methods: {
    allowNumbersOnly(event) {
      const allowed = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"];
      if (allowed.includes(event.key)) return;
      if (!/^\d$/.test(event.key)) {
        event.preventDefault();
      }
    },
    async loadSettings() {
      this.loading = true;
      try {
        const response = await client.graphql({
          query: get_auto_close_days,
        });
        const responseData = JSON.parse(response.data.get_auto_close_days);
        if (responseData.status == "SUCCESS") {
          const data = responseData.data ?? responseData;
          const days =
            typeof data === "number"
              ? data
              : Number(
                  data.ticket_auto_close_days ??
                    data.days ??
                    data.autoCloseDays ??
                    data.auto_close_days
                );
          if (Number.isFinite(days) && days >= 1) {
            this.autoCloseDays = days;
          }
        }
      } catch (error) {
        console.error("Error loading settings:", error);
        this.snackbar = {
          show: true,
          message: error.message || "Failed to load settings",
          color: "error",
        };
      } finally {
        this.loading = false;
      }
    },
    async saveSettings() {
      const days = Number(this.autoCloseDays);
      if (!Number.isFinite(days) || days < 1 || !Number.isInteger(days)) {
        this.snackbar = {
          show: true,
          message: "Enter a valid number of days (1 or more).",
          color: "warning",
        };
        return;
      }

      this.saving = true;
      const input = {
        ticket_auto_close_days: days,
      };
      try {
        const response = await client.graphql({
          query: set_auto_close_days,
          variables: {
            input: input,
          },
        });
        const responseData = JSON.parse(response.data.set_auto_close_days);
        if (responseData.status == "SUCCESS") {
          this.saving = false;
          this.autoCloseDays = days;
          this.snackbar = {
            show: true,
            message:
              responseData.status_message ||
              `Auto-close set to ${days} day${days === 1 ? "" : "s"}.`,
            color: "success",
          };
        } else {
          this.saving = false;
          this.snackbar = {
            show: true,
            message:
              responseData.status_message || "Failed to update auto-close days",
            color: "error",
          };
        }
      } catch (error) {
        this.saving = false;
        console.error("Error updating auto-close days:", error);
        this.snackbar = {
          show: true,
          message: error.message || "Failed to update auto-close days",
          color: "error",
        };
      }
    },
  },
};
</script>

<style scoped>
.settings-page {
  background: #fff;
  border-radius: 8px;
  padding: 8px 8px 24px;
}

.setting-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  border: 1px solid #e8edf2;
  border-radius: 12px;
  padding: 20px 22px;
  background: #fff;
}

.setting-info {
  min-width: 0;
  flex: 1;
  max-width: 720px;
}

.setting-title {
  font-size: 16px;
  font-weight: 700;
  color: #1a237e;
  margin-bottom: 6px;
}

.setting-desc {
  font-size: 13px;
  line-height: 1.45;
  color: rgba(0, 0, 0, 0.55);
}

.setting-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.days-input {
  width: 72px;
}

.days-input :deep(.v-field) {
  background: #f5f7fa;
  border-radius: 8px;
}

.days-input :deep(input) {
  text-align: center;
}

.days-label {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.55);
}

.save-btn {
  border-radius: 999px !important;
  min-width: 88px;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
}

@media (max-width: 700px) {
  .setting-controls {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
